import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Mic, MicOff, Send, FileText } from 'lucide-react';
import { MobileBottomSheet } from './MobileBottomSheet';
import { QuickTemplates } from './QuickTemplates';
import { useVoiceToText } from '@/hooks/useVoiceToText';
import { useBoardContext } from '@/contexts/BoardContext';
import { useToast } from '@/hooks/use-toast';

interface MobileCardCreatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnId: string;
}

export const MobileCardCreator = ({ 
  open, 
  onOpenChange, 
  columnId 
}: MobileCardCreatorProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);

  const { createCard } = useBoardContext();
  const { toast } = useToast();
  const { 
    isListening, 
    isSupported: voiceSupported, 
    transcript, 
    startListening, 
    stopListening 
  } = useVoiceToText();

  useEffect(() => {
    if (transcript && isVoiceMode) {
      if (!title) {
        setTitle(transcript);
      } else {
        setDescription(prev => prev ? `${prev} ${transcript}` : transcript);
      }
    }
  }, [transcript, isVoiceMode, title]);

  const handleSubmit = () => {
    if (!title.trim()) {
      toast({
        title: "Title required",
        description: "Please enter a title for the card.",
        variant: "destructive",
      });
      return;
    }

    createCard({
      title: title.trim(),
      description: description.trim() || null,
      column_id: columnId,
      category: category.trim() || null,
    });

    // Reset form
    setTitle('');
    setDescription('');
    setCategory('');
    setShowTemplates(false);
    setIsVoiceMode(false);
    onOpenChange(false);

    toast({
      title: "Card created",
      description: "Your new card has been added to the board.",
    });
  };

  const handleTemplateSelect = (template: any) => {
    setTitle(template.title);
    setDescription(template.description || '');
    setCategory(template.category || '');
    setShowTemplates(false);
  };

  const handleVoiceToggle = () => {
    if (isListening) {
      stopListening();
      setIsVoiceMode(false);
    } else {
      setIsVoiceMode(true);
      startListening();
    }
  };

  return (
    <MobileBottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Create New Card"
      description="Add a new card to your board"
    >
      <div className="space-y-4 p-4">
        {showTemplates ? (
          <div className="space-y-4">
            <QuickTemplates onSelectTemplate={handleTemplateSelect} />
            <Button
              variant="outline"
              onClick={() => setShowTemplates(false)}
              className="w-full"
            >
              Create Custom Card
            </Button>
          </div>
        ) : (
          <>
            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTemplates(true)}
                className="flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                Templates
              </Button>
              
              {voiceSupported && (
                <Button
                  variant={isListening ? "destructive" : "outline"}
                  size="sm"
                  onClick={handleVoiceToggle}
                  className="flex items-center gap-2"
                >
                  {isListening ? (
                    <>
                      <MicOff className="h-4 w-4" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Mic className="h-4 w-4" />
                      Voice
                    </>
                  )}
                </Button>
              )}
            </div>

            {isListening && (
              <div className="bg-red-50 dark:bg-red-950 p-3 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-300">
                  🎤 Listening... Speak now to add content
                </p>
              </div>
            )}

            <Separator />

            {/* Form Fields */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter card title..."
                  className="mt-1"
                  autoFocus={!isVoiceMode}
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add description..."
                  rows={3}
                  className="mt-1 resize-none"
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Optional category..."
                  className="mt-1"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!title.trim()}
                className="flex-1 flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                Create Card
              </Button>
            </div>
          </>
        )}
      </div>
    </MobileBottomSheet>
  );
};
